import { calculatePrice } from './priceHandler';
import { ExtendedWarrantyLineConfig, ExtendedWarrantyOutput } from '../types';

export function mergeConfigState(
  warrantyInput: ExtendedWarrantyLineConfig[],
  configState: ExtendedWarrantyOutput,
  currency: string
): ExtendedWarrantyOutput {
  // First, for each entry in configState, we will check if we have a matching entry in warranty input
  let mergedState = Object.keys(configState).reduce((newState, warrantedPartNumber) => {
    const warrantyEntry = warrantyInput.find(
      (warranty) => warranty.warrantedPartNumber === warrantedPartNumber
    );

    // If the warrantedPartNumber is no longer in the quote, we can remove the warrantedPartNumber from configState
    if (!warrantyEntry) {
      return newState;
    }

    const stateEntry = configState[warrantedPartNumber];

    // If the selected duration is no longer valid, we have to reset it
    const warrantyOption = warrantyEntry.warrantyOptions.find(
      (option) => option.duration === stateEntry.warrantyDuration
    );

    if (!warrantyOption) {
      // We also make sure the possibly new warranted quantity is updated
      stateEntry.warrantedQuantity = warrantyEntry.warrantedQuantity;
      stateEntry.warrantyDuration = 0;
      stateEntry.warrantyPrice = 0;

      return { ...newState, [stateEntry.warrantedPartNumber]: stateEntry };
    }

    // If the warrantedPartNumber has valid duration BUT changed quantity, we have to update the configState:
    if (stateEntry.warrantedQuantity !== warrantyEntry.warrantedQuantity) {
      const { duration, price } = warrantyOption;

      stateEntry.warrantedQuantity = warrantyEntry.warrantedQuantity;
      stateEntry.warrantyDuration = duration;
      // Recalculate the warranty total price according to the new warranted quantity
      stateEntry.warrantyPrice = calculatePrice({
        warrantedQuantity: warrantyEntry.warrantedQuantity,
        warrantyDuration: duration,
        warrantyPrice: price,
      });
    }

    return { ...newState, [stateEntry.warrantedPartNumber]: stateEntry };
  }, configState);

  // Now, for each new entry in the warranty input that is NOT in the configState,
  // we initialize the warranty with None duration
  mergedState = warrantyInput.reduce((newState, warranty) => {
    if (newState[warranty.warrantedPartNumber]) {
      return newState;
    }

    return {
      ...newState,
      [warranty.warrantedPartNumber]: {
        currency,
        warrantedPartNumber: warranty.warrantedPartNumber,
        warrantedQuantity: warranty.warrantedQuantity,
        warrantyPartNumber: warranty.warrantyPartNumber,
        // Initialize with duration 0 (no ext. warranty)
        warrantyDuration: 0,
        warrantyPrice: 0,
      },
    };
  }, mergedState);

  return mergedState;
}
